import * as React from 'react';
import { connect } from 'react-redux';
import { createSelector } from 'reselect';
import { isString } from 'underscore';

import { Navigator } from 'components/Wizard';
import { defaultTranscriptionEnabledSelector } from 'redux/modules/display-pref';
import {
  clearEpisodeWizard,
  createEpisode,
} from 'redux/modules/episode-wizard';
import * as mixpanelActions from 'redux/modules/mixpanel';
import { getMixpanelBlurValue } from 'redux/modules/mixpanel/utils';
import { showError } from 'redux/modules/notification/actions';
import { clearSearchResults } from 'redux/modules/podcast-search';
import { fullEpisodeCaptionEnabledSelector } from 'redux/modules/pricing';
import { goToCreate } from 'redux/modules/router/actions';
import { Dispatch } from 'redux/types';
import { ImageOriginId, SoundwavePositionValue } from 'types';
import { isApplicationError } from 'utils/ApplicationError';
import { getDuration } from 'utils/audio';
import { DropZoneType } from 'utils/constants';
import {
  getBlurRadius,
  getBorderRadius,
  hasSlideFromOrigin,
} from 'utils/embed/embed';
import {
  getPositionIdFromWave,
  mapSoundwaveGeneration,
} from 'utils/embed/soundwave';
import { FatalError } from 'utils/FatalError';
import { MIXPANEL_WIZARD_STEP_MAP } from './constants';
import EpisodeWizard from './EpisodeWizard';
import { EpisodeWizardProps as Props } from './types';

type DispatchProps = Pick<
  Props,
  'onCancel' | 'onError' | 'onMount' | 'onStepChange' | 'onSubmit' | 'onUnmount'
>;

const ERROR_ID = 'episode-wizard-error';

const defaultTranscriptionSelector = createSelector(
  defaultTranscriptionEnabledSelector,
  enabled => ({
    language: 'en-US',
    transcribe: !!enabled,
    transcriptUrl: undefined,
  }),
);

const mapStateToProps = state => ({
  fullEpisodeCaptionEnabled: fullEpisodeCaptionEnabledSelector(state),
  defaultTranscription: defaultTranscriptionSelector(state),
});

const mapDispatchToProps = (dispatch: Dispatch): DispatchProps => ({
  onCancel: (nav: Navigator) => {
    dispatch(
      mixpanelActions.onCancelWizard(
        'episode',
        MIXPANEL_WIZARD_STEP_MAP[nav.stepId],
      ),
    );
    dispatch(goToCreate());
  },
  onError: (err, file = undefined) => {
    const message = isString(err) ? err : err.message;
    const code = !isString(err) && isApplicationError(err) ? err.code : null;

    if (file) {
      dispatch(
        mixpanelActions.onFileUpload({
          dropZoneType: DropZoneType.EPISODE,
          source: 'localFile',
          file,
          error: message,
        }),
      );
    }

    if (err instanceof FatalError) {
      dispatch(
        showError({
          message: 'There was an error creating your video',
          code: 'ER003',
          id: ERROR_ID,
        }),
      );
      dispatch(goToCreate());
    } else {
      dispatch(showError({ message, code, id: ERROR_ID }));
    }
  },
  onMount: () => dispatch(mixpanelActions.clearMixpanel()),
  onStepChange: (to, from, { templateId, audioSource } = {}) => {
    if (from && to.stepId !== 'loading') {
      dispatch(
        mixpanelActions.onWizardNext({
          templateId,
          step: MIXPANEL_WIZARD_STEP_MAP[from.stepId],
          type: 'episode',
          audioSource,
        }),
      );
    }
  },
  onSubmit: async config => {
    const {
      audioSource,
      backgroundColor,
      slideshow,
      textOverlays,
      transcription,
    } = config;
    const originalAudioDuration = await getDuration(audioSource);
    const waveOptions = {
      waveColor: config.soundwave?.waveColor,
      waveGeneration: mapSoundwaveGeneration(config.soundwave?.waveGeneration),
      wavePosition: getPositionIdFromWave(
        config.soundwave,
      ) as SoundwavePositionValue,
      waveStyle: config.soundwave?.waveType,
      waveformPrefId: config.soundwave?.waveformPrefId,
    };

    dispatch(
      mixpanelActions.onCompleteWizard({
        backgroundColor,
        originalAudioDuration,
        addedImage: slideshow?.length > 0,
        addedText: textOverlays?.length > 0,
        audioSource: config.audioSourceType,
        canvaImage: hasSlideFromOrigin(ImageOriginId.CANVA, config),
        templateId: config.templateId,
        transcription: transcription?.transcribe,
        type: 'episode',
        blurValue: getMixpanelBlurValue(getBlurRadius(config)),
        borderRadiusValue: getBorderRadius(config),
        ...waveOptions,
      }),
    );

    await dispatch(createEpisode(config));
  },
  onUnmount: () => {
    dispatch(clearEpisodeWizard());
    dispatch(clearSearchResults());
  },
});

const component = connect(mapStateToProps, mapDispatchToProps)(EpisodeWizard);
export type EpisodeWizardProps = React.ComponentPropsWithoutRef<
  typeof component
>;
export default component;
